import { useEffect, useRef } from 'preact/hooks';
import { Fragment } from 'preact';
import { createPortal } from 'preact/compat';

export function Tools3() {
  const canvasRef = useRef(null);
  const unitSelectRef = useRef(null);
  const inchScaleSelectRef = useRef(null);
  const inchScaleLabelRef = useRef(null);
  const inputX1Ref = useRef(null);
  const inputX2Ref = useRef(null);
  const setGuidesRef = useRef(null);
  const clearRef = useRef(null);
  const lockRef = useRef(null);
  const downloadRef = useRef(null);

  const leftX = useRef(100);
  const rightX = useRef(300);
  const dragging = useRef(false);
  const dragTarget = useRef(null);
  const startX = useRef(null);
  const isGuideLock = useRef(false);

  useEffect(() => {
    const dpi = window.devicePixelRatio || 1;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const unitSelect = unitSelectRef.current;
    const inchScaleSelect = inchScaleSelectRef.current;
    const inchScaleLabel = inchScaleLabelRef.current;

    const dpiBase = +localStorage.getItem("dpiBase") || 96;

    function getUnitToPx(unit) {
      switch (unit) {
        case "cm": return (dpiBase / 2.54) * dpi;
        case "mm": return (dpiBase / 2.54) * dpi;
        case "in": return dpiBase * dpi;
      }
    }

    function setupCanvas() {
      const width = document.querySelector(".ruler-box").getBoundingClientRect().width;
      const height = document.querySelector(".ruler-box").getBoundingClientRect().height - 100;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      canvas.width = width * dpi;
      canvas.height = height * dpi;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpi, dpi);
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width * 2, canvas.height * 2);
    }

    function drawRulers(unit, inchScale = 16) {
      const unitPx = getUnitToPx(unit) / dpi;
      const divisions = unit === "in" ? inchScale : 10;
      const step = unitPx / divisions;
      const width = document.querySelector(".ruler-box").getBoundingClientRect().width;
      const height = document.querySelector(".ruler-box").getBoundingClientRect().height - 100;

      const x1 = Math.min(leftX.current, rightX.current);
      const x2 = Math.max(leftX.current, rightX.current);

      ctx.fillStyle = "rgba(0, 0, 255, 0.2)";
      ctx.fillRect(x1, 0, x2 - x1, height);

      ctx.fillStyle = "black";
      ctx.strokeStyle = "black";

      const drawHorizontal = (yOffset, flip = false) => {
        for (let i = 0; i < width / unitPx; i++) {
          const baseX = i * unitPx;
          for (let j = 0; j < divisions; j++) {
            let x = baseX + j * step;
            let h = 6;
            let label = "";

            if (unit === "in") {
              if (j % (divisions / 2) === 0) {
                h = 16;
                if (j === divisions / 2) label = "½";
              } else if (j % (divisions / 4) === 0) {
                h = 14;
                if (j === divisions / 4) label = "¼";
                else if (j === (divisions * 3) / 4) label = "¾";
              } else if (j % (divisions / 8) === 0) h = 12;
              else if (j % (divisions / 16) === 0) h = 10;
            } else {
              if (j === 5) h = 12;
            }

            ctx.beginPath();
            ctx.moveTo(x, yOffset);
            ctx.lineTo(x, yOffset + (flip ? -h : h));
            ctx.stroke();

            if (label) {
              ctx.font = "10px sans-serif";
              ctx.textAlign = "center";
              ctx.textBaseline = flip ? "bottom" : "top";
              ctx.fillText(label, x, yOffset + (flip ? -h - 2 : h + 10));
            }
          }

          ctx.beginPath();
          ctx.moveTo(baseX + unitPx, yOffset);
          ctx.lineTo(baseX + unitPx, yOffset + (flip ? -20 : 20));
          ctx.stroke();

          ctx.font = "11px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          const labelText = (unit === "mm" ? (i + 1) * 10 : i + 1);
          ctx.fillText(labelText, baseX + unitPx, yOffset + (flip ? -32 : 24));
        }
      };

      drawHorizontal(0);
      drawHorizontal(height, true);

      const unitPxFull = getUnitToPx(unit);
      const val = ((x2 - x1) * dpi / unitPxFull) * (unit === "mm" ? 10 : 1);
      const x1Val = (x1 * dpi / unitPxFull) * (unit === "mm" ? 10 : 1);
      const x2Val = (x2 * dpi / unitPxFull) * (unit === "mm" ? 10 : 1);
      ctx.fillStyle = "blue";
      ctx.font = "16px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const text = `${val.toFixed(2)} ${unit}`;
      ctx.fillText(text, (x1 + x2) / 2, height / 2);

      inputX1Ref.current.value = x1Val.toFixed(2);
      inputX2Ref.current.value = x2Val.toFixed(2);
    }

    function render() {
      const unit = unitSelect.value;
      const inchScale = parseInt(inchScaleSelect.value) || 16;
      inchScaleLabel.style.display = unit === "in" ? "inline" : "none";
      setupCanvas();
      drawRulers(unit, inchScale);
    }

    canvas.addEventListener("mousedown", e => {
      if (isGuideLock.current) return;

      const x = e.offsetX;
      if (Math.abs(x - leftX.current) < Math.abs(x - rightX.current)) dragTarget.current = "left";
      else dragTarget.current = "right";
      startX.current = x;
      dragging.current = true;
    });

    canvas.addEventListener("mousemove", e => {
      if (!dragging.current || !dragTarget.current) return;
      const delta = e.offsetX - startX.current;
      startX.current = e.offsetX;
      const width = document.querySelector(".ruler-box").getBoundingClientRect().width;
      if (dragTarget.current === "left") leftX.current = Math.min(Math.max(0, leftX.current + delta), width);
      else rightX.current = Math.min(Math.max(0, rightX.current + delta), width);
      render();
    });

    canvas.addEventListener("mouseup", () => {
      dragging.current = false;
      dragTarget.current = null;
    });

    canvas.addEventListener("mouseleave", () => {
      dragging.current = false;
      dragTarget.current = null;
    });

    canvas.addEventListener("touchstart", e => {
      if (isGuideLock.current) return;

      const x = e.touches[0].clientX;
      if (Math.abs(x - leftX.current) < Math.abs(x - rightX.current)) dragTarget.current = "left";
      else dragTarget.current = "right";
      startX.current = x;
      dragging.current = true;
    });

    canvas.addEventListener("touchmove", e => {
      if (!dragging.current || !dragTarget.current) return;
      const x = e.touches[0].clientX;
      const delta = x - startX.current;
      startX.current = x;

      const width = document.querySelector(".ruler-box").getBoundingClientRect().width;
      if (dragTarget.current === "left") leftX.current = Math.min(Math.max(0, leftX.current + delta), width);
      else rightX.current = Math.min(Math.max(0, rightX.current + delta), width);
      render();
    });

    canvas.addEventListener("touchend", () => {
      dragging.current = false;
      dragTarget.current = null;
    });

    setGuidesRef.current.addEventListener("click", () => {
      const x1Val = parseFloat(inputX1Ref.current.value);
      const x2Val = parseFloat(inputX2Ref.current.value);
      const width = document.querySelector(".ruler-box").getBoundingClientRect().width;
      const unit = unitSelect.value;
      const pxPerUnit = getUnitToPx(unit);
      rightX.current = x1Val >= 0 && !isNaN(x1Val) ? (x1Val / (unit === "mm" ? 10 : 1)) * pxPerUnit / dpi : null;
      leftX.current = x2Val >= 0 && !isNaN(x2Val) ? (x2Val / (unit === "mm" ? 10 : 1)) * pxPerUnit / dpi : null;
      rightX.current = Math.max(0, Math.min(rightX.current, width));
      leftX.current = Math.max(0, Math.min(leftX.current, width));
      render();
    });

    clearRef.current.addEventListener("click", () => {
      leftX.current = 100;
      rightX.current = 300;

      isGuideLock.current = false;

      render();
    });

    lockRef.current.addEventListener("click", () => {
      isGuideLock.current = !isGuideLock.current;

      if (isGuideLock.current == true) {
        //unlock
        lockRef.current.innerText = langText["text_ruler_unlock"];
      } else {
        //lock
        lockRef.current.innerText = langText["text_ruler_lock"];
      }
    });

    downloadRef.current.addEventListener("click", () => {
      const dataURL = canvas.toDataURL("image/png");

      const link = document.createElement('a');
      link.href = dataURL;
      link.download = 'ruler-image.png';
      link.click();
    });

    unitSelect.addEventListener("change", render);
    inchScaleSelect.addEventListener("change", render);
    window.addEventListener("resize", render);


    render();

    return () => {
      window.removeEventListener("resize", render);
      unitSelect.removeEventListener("change", render);
      inchScaleSelect.removeEventListener("change", render);
    }
  }, []);

  return (
    <>
      <canvas ref={canvasRef}></canvas>
      {createPortal(

        <div class="controls" style={{ bottom: '60px', direction: "ltr" }}>
          <label>
            {langText['text_ruler_unit']}:
            <select ref={unitSelectRef}>
              <option value="mm">{langText["text_ruler_cm"]}</option>
              <option value="mm">{langText["text_ruler_mm"]}</option>
              <option value="mm">{langText["text_ruler_in"]}</option>
            </select>
          </label>

          <label ref={inchScaleLabelRef} style={{ display: 'none' }}>
            {langText['text_ruler_in_scale']}:
            <select ref={inchScaleSelectRef}>
              <option value="2">1/2</option>
              <option value="4">1/4</option>
              <option value="8">1/8</option>
              <option value="16" selected>1/16</option>
              <option value="32">1/32</option>
            </select>
          </label>

          <div style={{ marginTop: '10px' }}>
            <label>
              X1:
              <input type="number" ref={inputX1Ref} style={{ width: '60px' }} />
            </label>
            <label style={{ marginRight: '10px' }}>
              X2:
              <input type="number" ref={inputX2Ref} style={{ width: '60px' }} />
            </label>
            <button ref={setGuidesRef}>{langText["text_ruler_set"]}</button>
          </div>

          <div className='action' style={{ marginTop: '10px' }}>
            <button ref={clearRef}>{langText['text_ruler_clear']}</button>
            <button id='lockBtn' ref={lockRef}>{langText['text_ruler_lock']}</button>
            <button ref={downloadRef}>{langText['text_ruler_download_iamge']}</button>
          </div>
        </div>
        ,
        document.getElementById('ruler-controller')
      )}
    </>
  );
}
